import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import axios from 'axios';
import { Upload, Plus, Search, FileText, Send, AlertTriangle } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const API = `${BACKEND_URL}/api`;

const DocumentsUpload = () => {
  const [documents, setDocuments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewDocument, setShowNewDocument] = useState(false);
  const [newDocument, setNewDocument] = useState({
    guest_name: '',
    document_type: '',
    document_number: '',
    property_id: '',
    booking_id: '',
    file_url: ''
  });

  useEffect(() => {
    fetchDocuments();
    fetchProperties();
    fetchBookings();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.filter(booking => booking.status === 'confirmed'));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/documents`, newDocument, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewDocument(false);
      setNewDocument({
        guest_name: '',
        document_type: '',
        document_number: '',
        property_id: '',
        booking_id: '',
        file_url: ''
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error creating document:', error);
    }
  };

  const filteredDocuments = documents.filter(document => 
    document.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    document.document_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? `${property.name} - ${property.city}` : 'Immobile non trovato';
  };

  if (loading) {
    return (
      <Layout title="Upload Documenti">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Upload Documenti">
      <div className="space-y-6 fade-in">
        {/* Header with actions */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Documenti Ospiti</h2>
              <p className="text-sm text-slate-600">Carica e gestisci i documenti degli ospiti</p>
            </div>
            
            <Dialog open={showNewDocument} onOpenChange={setShowNewDocument}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-900 hover:bg-blue-800"
                  data-testid="add-document-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Documento
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Nuovo Documento Ospite</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateDocument} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome Ospite</Label>
                      <Input
                        value={newDocument.guest_name}
                        onChange={(e) => setNewDocument({...newDocument, guest_name: e.target.value})}
                        required
                        placeholder="Mario Rossi"
                        data-testid="guest-name-input"
                      />
                    </div>
                    <div>
                      <Label>Tipo Documento</Label>
                      <Select 
                        value={newDocument.document_type}
                        onValueChange={(value) => setNewDocument({...newDocument, document_type: value})}
                      >
                        <SelectTrigger data-testid="document-type-select">
                          <SelectValue placeholder="Seleziona tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="passport">Passaporto</SelectItem>
                          <SelectItem value="id_card">Carta d'Identità</SelectItem>
                          <SelectItem value="driving_license">Patente</SelectItem>
                          <SelectItem value="other">Altro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Numero Documento</Label>
                      <Input
                        value={newDocument.document_number}
                        onChange={(e) => setNewDocument({...newDocument, document_number: e.target.value})}
                        required
                        placeholder="AB123456"
                        data-testid="document-number-input"
                      />
                    </div>
                    <div>
                      <Label>Immobile</Label>
                      <Select 
                        value={newDocument.property_id}
                        onValueChange={(value) => setNewDocument({...newDocument, property_id: value})}
                      >
                        <SelectTrigger data-testid="property-select">
                          <SelectValue placeholder="Seleziona immobile" />
                        </SelectTrigger>
                        <SelectContent>
                          {properties.map(property => (
                            <SelectItem key={property.id} value={property.id}>
                              {property.name} - {property.city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Prenotazione</Label>
                      <Select 
                        value={newDocument.booking_id}
                        onValueChange={(value) => setNewDocument({...newDocument, booking_id: value})}
                      >
                        <SelectTrigger data-testid="booking-select">
                          <SelectValue placeholder="Seleziona prenotazione" />
                        </SelectTrigger>
                        <SelectContent>
                          {bookings.map(booking => (
                            <SelectItem key={booking.id} value={booking.id}>
                              {booking.guest_name} - {new Date(booking.check_in).toLocaleDateString('it-IT')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>URL File Documento</Label>
                      <Input
                        value={newDocument.file_url}
                        onChange={(e) => setNewDocument({...newDocument, file_url: e.target.value})}
                        required
                        placeholder="https://example.com/documento.pdf"
                        data-testid="file-url-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewDocument(false)}
                    >
                      Annulla
                    </Button>
                    <Button 
                      type="submit"
                      className="bg-blue-900 hover:bg-blue-800"
                      data-testid="save-document-btn"
                    >
                      Carica Documento
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Search */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
            <Input
              placeholder="Cerca per nome ospite o numero documento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-documents-input"
            />
          </div>
        </Card>

        {/* Alloggiatiweb Integration Info */}
        <Card className="p-6 border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-start space-x-4">
            <AlertTriangle className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Integrazione Alloggiatiweb</h3>
              <p className="text-blue-800 text-sm mb-3">
                I documenti caricati verranno automaticamente inviati al portale Alloggiatiweb 
                per la comunicazione con la Questura di competenza.
              </p>
              <div className="flex items-center space-x-4 text-sm">
                <Badge className="bg-blue-100 text-blue-800">API: In fase di sviluppo</Badge>
                <Badge className="bg-amber-100 text-amber-800">Invio manuale temporaneo</Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Documents list */}
        <div className="grid gap-4">
          {filteredDocuments.length === 0 ? (
            <Card className="p-8 text-center border-0 shadow-lg">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Nessun documento trovato</h3>
              <p className="text-slate-500">
                {searchTerm 
                  ? 'Prova a modificare il termine di ricerca' 
                  : 'Inizia caricando il primo documento ospite'
                }
              </p>
            </Card>
          ) : (
            filteredDocuments.map((document) => (
              <Card key={document.id} className="p-6 border-0 shadow-lg card-hover" data-testid={`document-card-${document.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-slate-900">{document.guest_name}</h3>
                      {document.submitted_to_police ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Inviato alla Questura</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">Da Inviare</Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-600">
                      <span className="font-medium">Tipo:</span> {document.document_type} | 
                      <span className="font-medium ml-2">Numero:</span> {document.document_number}
                    </div>
                    <div className="text-sm text-slate-500">
                      <span className="font-medium">Immobile:</span> {getPropertyName(document.property_id)}
                    </div>
                    <div className="text-sm text-slate-500">
                      <span className="font-medium">Caricato il:</span> {new Date(document.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(document.file_url, '_blank')}
                      data-testid={`view-document-${document.id}`}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Visualizza
                    </Button>
                    {!document.submitted_to_police && (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => alert('Integrazione Alloggiatiweb in fase di sviluppo')}
                        data-testid={`submit-document-${document.id}`}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Invia alla Questura
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DocumentsUpload;